import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAIAgentSidebarActions } from '@/hooks/ai-agents/useAIAgentSidebarActions';
import ProfilePicture from './ProfilePicture';
import HeaderContent from './HeaderContent';
import { ChatHeaderProps } from './types';
import { cardVariants, layoutTransition } from './animations';
import { getContainerStyles } from './styles';
import { GradientAnimationStyles } from './GradientAnimationStyles';

/**
 * ChatHeader is a dynamic component that morphs between:
 * 1. A welcome card when no messages are present (expanded mode)
 * 2. A compact header at the top of the chat when conversation is active (compact mode)
 */
export const ChatHeaderContainer: React.FC<ChatHeaderProps> = ({
  activeAssistant,
  variant = 'default',
  isCompact = false,
  className,
  onClose
}) => {
  const navigate = useNavigate();
  const { emit } = useAIAgentSidebarActions();
  
  const handleContainerClick = () => {
    if (onClose) onClose(); // Close modal first
    
    // Navigate to AI agents page and switch to threads tab
    emit('switchToThreadsTab'); // Switch to threads section
    navigate('/account/ai-agents');
    
    console.log('[ChatHeader] Navigation triggered - switching to threads section');
  };
  
  const currentContainerStyles = React.useMemo(
    () => getContainerStyles(variant, isCompact),
    [variant, isCompact]
  );

  const shouldRenderContent = activeAssistant !== null;

  return (
    <div 
      className={cn(
        currentContainerStyles,
        "flex", // Always flex
        isCompact 
          ? "items-center justify-center" // Center content vertically and horizontally when compact
          : "items-start justify-center", // Align to top but center horizontally when not compact
        className
      )}
      data-testid="chat-header"
      data-variant={variant}
      data-compact={isCompact}
    >
      {shouldRenderContent && (
        <motion.div
          key={activeAssistant?.id || 'default-assistant'}
          className={cn(
            "w-full"
          )}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={cardVariants}
          layoutId="chat-header-card"
          transition={{
            layout: layoutTransition
          }}
          style={{ 
            perspective: "1000px", 
            transformStyle: "preserve-3d" 
          }}
        >
          <div 
            className={cn(
              "relative overflow-hidden transition-all duration-300 cursor-pointer group",
              "backdrop-blur-xl backdrop-saturate-150",
              isCompact 
                ? "p-2 rounded-lg bg-gradient-to-br from-white/50 via-white/20 to-white/10 dark:from-black/40 dark:to-black/20 hover:from-white/70 hover:via-white/30 hover:to-white/10 dark:hover:from-black/50 dark:hover:to-black/30" 
                : "p-6 rounded-2xl bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 hover:from-white/80 hover:via-white/40 hover:to-white/20 dark:hover:from-black/50 dark:hover:to-black/30"
            )}
            onClick={handleContainerClick}
            data-testid="copilot-container"
          >
            <motion.div 
              className={cn(
                "absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-blue-100/20 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-blue-900/20 gradient-animation transition-all duration-500",
                isCompact ? "h-[120px]" : "h-full",
                "group-hover:from-blue-100/40 group-hover:via-purple-100/40 group-hover:to-blue-100/40 dark:group-hover:from-blue-900/30 dark:group-hover:via-purple-900/30 dark:group-hover:to-blue-900/30"
              )}
              layoutId="header-background"
              transition={{
                height: { 
                  type: "spring",
                  stiffness: 250,
                  damping: 30,
                  duration: 1.1
                }
              }}
            />

            <div className={cn(
              isCompact 
                ? "flex items-center" 
                : "block"
            )}>
              <ProfilePicture 
                activeAssistant={activeAssistant}
                isCompact={isCompact}
              />
              
              <HeaderContent 
                activeAssistant={activeAssistant}
                isCompact={isCompact}
              />
            </div>
          </div>
        </motion.div>
      )}
      
      <GradientAnimationStyles />
    </div>
  );
};

export default ChatHeaderContainer;
